// Imports for external libraries go here.
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { useRouter } from 'next/router';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Button } from '@marriott/mi-ui-library';
import {
  ASubheader,
  ASubheaderSize,
  MButton,
  MButtonType,
  MButtonSize,
  MButtonIconPlacement,
} from '@marriott/mi-web-ui-kit';
import { CardComparisonProps, CardComparisonCategory, CardComparisonData } from './CardComparison.types';
import { StyledCardComparison } from './CardComparison.styles';
import { TableRow } from '../../molecules/DataCellComponent';
import { ComparisonSlotDropdown } from '../../molecules/ComparisonSlotDropdown';
import { usePageContext } from '../../context/PageContext';
import {
  buildAllCardsListFromCobrandCards,
  buildComparisonSlotUrl,
  clearComparisonSlot,
  compactComparisonCardIds,
  getComparisonCardIdsFromUrl,
  getComparisonDropdownOptions,
  getResolvedDefaultComparisonCardIds,
  getStoredCreditCardEndpoint,
  isFromFOCComparisonEntry,
  selectComparisonCard,
  setCardArray,
  isMobile,
} from '../../modules/utils';
import type { CompareSelectionCard } from '../../modules/utils';
import { useGlobalNavVisibility } from '../../hooks';
import { CARD_COMPARISON_LIMITS, SESSION_CONSTANTS } from '../../modules/constants';
import { useStore } from '../../modules/store/cobrandStore';
import { CardComparisonAccordion } from '../../molecules/CardComparisonAccordion';
export const CardComparisonConfig = {
  emptyLabel: 'CardComparison',
  isEmpty: () => true,
  resourceType: `/components/content/CardComparison`,
};

const sortByCardOrder = (data: CardComparisonData[], order: string[]) => {
  return [...data].sort((a, b) => order.indexOf(a.cardId ?? '') - order.indexOf(b.cardId ?? ''));
};

const VIEWPORT_L_MIN_WIDTH = 992;

const sortCategories = (categories: CardComparisonCategory[], filteredCategoryIds: string[]) =>
  categories
    .filter(category => filteredCategoryIds.includes(category.categoryId ?? ''))
    .sort((a, b) => (a.categoryId ?? '').localeCompare(b.categoryId ?? ''));

const getResolvedViewportM = (): boolean | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  return window.innerWidth >= VIEWPORT_L_MIN_WIDTH;
};

const getPersistedCobrandData = (): Record<string, unknown> | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const persistedCobrandData = sessionStorage?.getItem(SESSION_CONSTANTS.COBRAND_DATA);
  if (!persistedCobrandData) {
    return undefined;
  }

  try {
    const parsedData = JSON.parse(persistedCobrandData);
    return typeof parsedData === 'object' && parsedData !== null && !Array.isArray(parsedData)
      ? (parsedData as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
};

const groupCategories = (categories: CardComparisonCategory[]) =>
  categories
    .reduce<{ id: string; value: string; count: number }[]>((acc, obj, index) => {
      const exists = acc.some(item => item.id === obj.groupId && item.value === obj.groupValue);
      if (!exists) {
        acc.push({ id: obj.groupId ?? 'unknown', value: obj.groupValue ?? '', count: index });
      }
      return acc;
    }, [])
    .sort((a, b) => a.id.localeCompare(b.id))
    .filter(group => group.id !== 'Header');

// Use named rather than default exports.
export const CardComparisonComp: FC<CardComparisonProps> = memo((props: CardComparisonProps) => {
  const router = useRouter();
  const styleclass = props?.styleclass;
  const pageContext = usePageContext();
  const { cardtype1, cardtype2, cardtype3 } = pageContext;
  const { cobrandData, setCobrandData } = useStore(store => store);
  const isEnhancedComparison =
    props.enableEnhancedCardComparison === true || props.enableEnhancedCardComparison === 'true';
  const { linkText, linkUrl, openInNewTab, trackingProperties, isGlobalNavSticky } = props;
  const isNavVisible = useGlobalNavVisibility(isGlobalNavSticky);
  const [prevEndPoint, setPrevEndPoint] = useState(linkUrl);
  const linkType = openInNewTab === 'true' ? 'external' : 'internal';
  const clickTrackValue = `${trackingProperties?.trackingContentPosition}|${trackingProperties?.trackingDescription}|${linkType}|${trackingProperties?.additionalTrackingVariables}`;

  useEffect(() => {
    const stored = getStoredCreditCardEndpoint(linkUrl);
    if (stored) {
      setPrevEndPoint(stored);
    }
  }, [linkUrl]);
  const routerQuery = router?.query;
  const [isClientReady, setIsClientReady] = useState(false);
  const [resolvedViewportM, setResolvedViewportM] = useState<boolean | undefined>();
  const activeSlotCount = useMemo(() => {
    if (!isEnhancedComparison) {
      return CARD_COMPARISON_LIMITS.MAX_CARDS_MOBILE;
    }
    if (!isClientReady || resolvedViewportM === undefined) {
      return CARD_COMPARISON_LIMITS.MAX_CARDS_DESKTOP;
    }
    const isMobileOrTablet = isMobile() || !resolvedViewportM;
    return isMobileOrTablet ? CARD_COMPARISON_LIMITS.MAX_CARDS_MOBILE : CARD_COMPARISON_LIMITS.MAX_CARDS_DESKTOP;
  }, [isClientReady, isEnhancedComparison, resolvedViewportM]);

  const cardIdFromURL = useMemo(
    () => [cardtype1, cardtype2, cardtype3].filter(Boolean),
    [cardtype1, cardtype2, cardtype3]
  );
  const cardtype1FromQuery = routerQuery?.['cardtype1'];
  const cardtype2FromQuery = routerQuery?.['cardtype2'];
  const cardtype3FromQuery = routerQuery?.['cardtype3'];
  const v2CardIdFromURL = useMemo(
    () =>
      getComparisonCardIdsFromUrl(
        router?.asPath,
        {
          cardtype1: cardtype1FromQuery ?? cardtype1,
          cardtype2: cardtype2FromQuery ?? cardtype2,
          cardtype3: cardtype3FromQuery ?? cardtype3,
        },
        activeSlotCount
      ),
    [
      activeSlotCount,
      cardtype1,
      cardtype1FromQuery,
      cardtype2,
      cardtype2FromQuery,
      cardtype3,
      cardtype3FromQuery,
      router?.asPath,
    ]
  );
  const [selectedCardIds, setSelectedCardIds] = useState<string[]>(v2CardIdFromURL);
  const hasUserClearedComparisonRef = useRef(false);
  const hasUserModifiedSelectionRef = useRef(false);

  useEffect(() => {
    const syncViewport = () => {
      setResolvedViewportM(getResolvedViewportM());
    };

    setIsClientReady(true);
    syncViewport();
    window.addEventListener('resize', syncViewport);

    return () => {
      window.removeEventListener('resize', syncViewport);
    };
  }, []);

  useEffect(() => {
    if (isEnhancedComparison && v2CardIdFromURL.length > 0) {
      setSelectedCardIds(v2CardIdFromURL);
      hasUserClearedComparisonRef.current = false;
      hasUserModifiedSelectionRef.current = false;
    }
  }, [isEnhancedComparison, v2CardIdFromURL]);

  const fromFOCQuery = routerQuery?.['fromFOC'];
  // Lock fromFOC to its value at mount — immune to URL tampering via shallow router.replace calls made
  // by card add/remove operations after page load.
  const isFromFOCRef = useRef(isFromFOCComparisonEntry(fromFOCQuery ?? pageContext?.fromFOC));
  const isFromFOC = isFromFOCRef.current;
  const defaultCardIds = useMemo(
    () =>
      getResolvedDefaultComparisonCardIds({
        pageItems: props.pageItems as Record<string, unknown>,
        defaultDesktopCardCodes: props.defaultDesktopCardCodes,
        defaultMobileCardCodes: props.defaultMobileCardCodes,
        slotLimit: activeSlotCount,
      }),
    [activeSlotCount, props.defaultDesktopCardCodes, props.defaultMobileCardCodes, props.pageItems]
  );
  const shouldDeferViewportDefaults =
    isEnhancedComparison && !isFromFOC && v2CardIdFromURL.length === 0 && resolvedViewportM === undefined;

  const comparisonCardIds = useMemo(() => {
    if (!isEnhancedComparison) {
      return cardIdFromURL;
    }

    if (hasUserModifiedSelectionRef.current) {
      return compactComparisonCardIds(selectedCardIds, activeSlotCount);
    }

    if (v2CardIdFromURL.length > 0) {
      return compactComparisonCardIds(v2CardIdFromURL, activeSlotCount);
    }

    const selectedIds = compactComparisonCardIds(selectedCardIds, activeSlotCount);
    if (selectedIds.length > 0) {
      return selectedIds;
    }

    if (shouldDeferViewportDefaults) {
      return [];
    }

    if (!isFromFOC && defaultCardIds.length > 0 && !hasUserClearedComparisonRef.current) {
      return defaultCardIds;
    }

    return [];
  }, [
    activeSlotCount,
    cardIdFromURL,
    defaultCardIds,
    isEnhancedComparison,
    isFromFOC,
    selectedCardIds,
    shouldDeferViewportDefaults,
    v2CardIdFromURL,
  ]);
  const selectedCategories = useMemo(
    () => (props.idValues.includes(',') ? props.idValues.split(',') : []),
    [props.idValues]
  );

  const categoryData = useMemo<CardComparisonCategory[]>(() => {
    const cats = props?.pageItems?.categories ? Object.values(props.pageItems.categories) : [];
    return cats as CardComparisonCategory[];
  }, [props?.pageItems?.categories]);

  const cardData = useMemo(
    () =>
      setCardArray(props?.pageItems).map(data => ({
        ...data,
        cardType: data.cardType ?? 'none',
      })),
    [props?.pageItems]
  );

  const filteredCategoryIds = useMemo(
    () => (selectedCategories.length ? selectedCategories : categoryData.map(category => category.categoryId ?? '')),
    [selectedCategories, categoryData]
  );

  const selectedCardsData = useMemo(
    () => cardData.filter(data => comparisonCardIds.includes(data.cardId ?? '')),
    [cardData, comparisonCardIds]
  );

  const sortedCategory = useMemo(
    () =>
      sortCategories(categoryData, filteredCategoryIds).filter(category =>
        selectedCardsData.some(data => data.categoryId === category.categoryId && data.dataType !== 'emptyField')
      ),
    [categoryData, filteredCategoryIds, selectedCardsData]
  );

  const sortedCategoryGroups = useMemo(() => groupCategories(sortedCategory), [sortedCategory]);

  const dataJson = useMemo(
    () => sortByCardOrder(selectedCardsData, comparisonCardIds),
    [selectedCardsData, comparisonCardIds]
  );

  const persistSelectedCards = useCallback(
    (nextSelectedCardIds: string[]) => {
      const persistedCobrandData = getPersistedCobrandData();
      const persistedSelectedCards = persistedCobrandData?.['selectedCards'];
      const currentSelectedCards = [
        ...(Array.isArray(cobrandData?.selectedCards) ? cobrandData.selectedCards : []),
        ...(Array.isArray(persistedSelectedCards) ? (persistedSelectedCards as CompareSelectionCard[]) : []),
      ];
      const selectedCards = nextSelectedCardIds.map(cardId => ({
        ...(currentSelectedCards.find(card => card?.cardname === cardId) ?? {}),
        cardname: cardId,
        checked: true,
      }));
      const nextCobrandData = {
        ...(persistedCobrandData ?? cobrandData),
        selectedCards,
      };

      setCobrandData(nextCobrandData as typeof cobrandData);

      if (typeof window !== 'undefined') {
        sessionStorage?.setItem(SESSION_CONSTANTS.COBRAND_DATA, JSON.stringify(nextCobrandData));
      }
    },
    [cobrandData, setCobrandData]
  );

  const persistSelectedCardsRef = useRef(persistSelectedCards);
  persistSelectedCardsRef.current = persistSelectedCards;

  const syncSelectedCardsToUrl = useCallback(
    (nextSelectedCardIds: string[]) => {
      const compactedCardIds = compactComparisonCardIds(nextSelectedCardIds, activeSlotCount);
      if (compactedCardIds.length === 0) {
        hasUserClearedComparisonRef.current = true;
      }
      hasUserModifiedSelectionRef.current = true;
      setSelectedCardIds(compactedCardIds);
      persistSelectedCards(compactedCardIds);
      router?.replace?.(
        buildComparisonSlotUrl({
          asPath: router?.asPath,
          pathname: router?.pathname,
          selectedCardIds: compactedCardIds,
        }),
        undefined,
        { shallow: true, scroll: false }
      );
    },
    [activeSlotCount, persistSelectedCards, router]
  );

  useEffect(() => {
    if (!isEnhancedComparison) {
      return;
    }

    if (
      v2CardIdFromURL.length > 0 ||
      isFromFOC ||
      hasUserModifiedSelectionRef.current ||
      hasUserClearedComparisonRef.current ||
      defaultCardIds.length === 0 ||
      shouldDeferViewportDefaults
    ) {
      return;
    }

    setSelectedCardIds(defaultCardIds);
    persistSelectedCardsRef.current(defaultCardIds);
  }, [defaultCardIds, isEnhancedComparison, isFromFOC, shouldDeferViewportDefaults, v2CardIdFromURL.length]);

  const hideComparisonContent = isEnhancedComparison && !comparisonCardIds.length;
  const [isScrolledPastTable, setIsScrolledPastTable] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const scrollSentinelRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node || typeof IntersectionObserver === 'undefined') {
      return;
    }

    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        const shouldHide = !entry.isIntersecting;
        setIsScrolledPastTable(prev => (prev === shouldHide ? prev : shouldHide));
      },
      { threshold: 0, rootMargin: '-52px 0px 0px 0px' }
    );
    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    const selector = isEnhancedComparison
      ? '.header-category'
      : '.header-category, .platinum-status, .gold-status, .silver-status, .card-image, .vertical-card-image';

    const elementsToToggle = document.querySelectorAll(selector);
    elementsToToggle.forEach(el => {
      el.classList.toggle('header-hidden', isScrolledPastTable);
    });

    return () => {
      // Ensure elements are restored if the effect re-runs or unmounts.
      elementsToToggle.forEach(el => {
        el.classList.remove('header-hidden');
      });
    };
  }, [isEnhancedComparison, isScrolledPastTable]);

  const [selectedCard, setSelectedCard] = useState('');

  const handleClearCard = useCallback(
    (slotIndex: number) => {
      setSelectedCard('');
      syncSelectedCardsToUrl(clearComparisonSlot(selectedCardIds, slotIndex, activeSlotCount));
    },
    [activeSlotCount, selectedCardIds, syncSelectedCardsToUrl]
  );

  const handleSelectCard = useCallback(
    (cardId: string) => {
      syncSelectedCardsToUrl(selectComparisonCard(selectedCardIds, cardId, activeSlotCount));
      setSelectedCard(cardId);
    },
    [activeSlotCount, selectedCardIds, syncSelectedCardsToUrl]
  );

  const allCardsList = useMemo(() => buildAllCardsListFromCobrandCards(props?.cobrandCards), [props?.cobrandCards]);

  const dropdownOptions = useMemo(
    () => getComparisonDropdownOptions(allCardsList, comparisonCardIds),
    [allCardsList, comparisonCardIds]
  );

  const getDataList = (category: CardComparisonCategory) => {
    const data = dataJson.filter(item => item.categoryId === category.categoryId);
    if (isEnhancedComparison) {
      return Array.from({ length: activeSlotCount }, (_, index) => {
        const cardId = comparisonCardIds[index];
        if (!cardId) {
          return {
            categoryId: category.categoryId,
            dataType: undefined,
          };
        }

        return data.find(item => item.cardId === cardId) ?? { categoryId: category.categoryId, dataType: undefined };
      });
    }

    return data;
  };

  const groupedCategories = useCallback(
    (categoryList: CardComparisonCategory[], groupId: string) =>
      categoryList.filter(category => category.groupId === groupId && category.groupId !== 'Header'),
    []
  );

  const headerRow = useMemo(() => sortedCategory.filter(category => category.groupId === 'Header'), [sortedCategory]);
  const slotControls = useMemo(
    () =>
      isEnhancedComparison
        ? Array.from({ length: activeSlotCount }, (_, index) => {
            const cardId = comparisonCardIds[index];
            if (cardId) {
              return (
                <button
                  aria-label={`Remove card ${index + 1} from comparison`}
                  className="comparison-slot-clear"
                  onClick={() => handleClearCard(index)}
                  type="button"
                >
                  <span className="icon-clear icon-xs" />
                </button>
              );
            }

            return (
              <div className="comparison-slot-empty comparison-slot-empty--enhanced p-4">
                <div className="comparison-slot-empty__dropdown">
                  <ComparisonSlotDropdown
                    options={dropdownOptions}
                    onChange={handleSelectCard}
                    placeholder="Select a Card"
                    selectedValue={selectedCard}
                    ariaLabel="Select a card to compare"
                  />
                </div>
                <div className="comparison-slot-empty__hint mt-3">
                  <ASubheader
                    text={`You can add up to ${activeSlotCount} cards to compare`}
                    size={ASubheaderSize.M}
                    className="t-subtitle-alt-m text-center"
                  />
                </div>
              </div>
            );
          })
        : undefined,
    [
      activeSlotCount,
      comparisonCardIds,
      dropdownOptions,
      handleClearCard,
      handleSelectCard,
      isEnhancedComparison,
      selectedCard,
    ]
  );

  return (
    <StyledCardComparison
      data-component-name="o-cobrand-CardComparison"
      data-testid="cobrand-CardComparison"
      className={styleclass}
      $hasStickyNav={isNavVisible}
      $isStuck={isScrolledPastTable}
    >
      <div className={'col-12 p-0'}>
        {!isEnhancedComparison && linkText && (
          <div className="d-flex justify-content-center justify-content-md-start">
            <MButton
              ctaLink={prevEndPoint}
              buttonType={MButtonType.Secondary}
              size={MButtonSize.M}
              className={'my-5'}
              data-testid="backbtn"
              trackingProperties={trackingProperties}
              custom_click_track_value={clickTrackValue}
              buttonText={linkText}
              iconPlacement={MButtonIconPlacement.Leading}
              iconClass="icon-back-arrow"
            />
          </div>
        )}
        {!hideComparisonContent && (
          <>
            <div ref={scrollSentinelRef} aria-hidden="true" className="scroll-sentinel" />
            <div
              className={clsx(
                'table-container col-12 p-0 mb-5',
                !isEnhancedComparison && 'mt-3',
                isEnhancedComparison && 'enhanced-table-container'
              )}
            >
              {dataJson && dataJson.length ? (
                <div className="table-wrapper col-12 p-0">
                  {headerRow.map((category: CardComparisonCategory) => {
                    const dataList = getDataList(category);
                    return (
                      <div
                        key={category.categoryId ?? category.groupId ?? category.categoryHeading}
                        className={`table-header align-items-center sticky-header`}
                      >
                        <TableRow
                          category={category}
                          dataList={dataList}
                          type="header"
                          cardIdFromURL={comparisonCardIds}
                          customClass="header-category"
                          columnCount={isEnhancedComparison ? activeSlotCount : undefined}
                          slotControls={slotControls}
                          isEnhancedComparison={isEnhancedComparison}
                        />
                      </div>
                    );
                  })}
                  {sortedCategoryGroups.map((group: { id: string; value: string; count: number }) => {
                    const categoriesToBeShown = groupedCategories(sortedCategory, group.id);
                    return (
                      <CardComparisonAccordion
                        key={group.id}
                        categoriesToBeShown={categoriesToBeShown}
                        group={group}
                        pageItems={props?.pageItems}
                        styleClass={clsx(isEnhancedComparison ? 'enhanced-accordion-wrapper' : 'accordion-wrapper')}
                        cardIdFromURL={isEnhancedComparison ? comparisonCardIds : undefined}
                        columnCount={isEnhancedComparison ? activeSlotCount : undefined}
                      />
                    );
                  })}
                </div>
              ) : (
                <div className="skeleton-loader ">
                  <Skeleton height="2.5rem" width="100%" />
                </div>
              )}
            </div>
            <div className={'d-flex justify-content-center'}>
              <Button
                isLink={true}
                href="#"
                className={clsx(isEnhancedComparison ? 'my-3' : 'my-5')}
                testId="buttonToTop"
              >
                Return to Top
                <span className="icon-up-arrow ml-1" />
              </Button>
            </div>
          </>
        )}
      </div>
    </StyledCardComparison>
  );
});

export const CardComparison = (props: CardComparisonProps) => (
  <EditableComponent config={CardComparisonConfig} {...props}>
    <CardComparisonComp {...props} />
  </EditableComponent>
);
